import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { RepairSchedule, WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { SUPPLIES_PAYMENT_METHOD_LABELS } from '../../shared/workflow.js';
import {
  Alert,
  Button,
  Card,
  DefinitionList,
  EmptyState,
  Field,
  InternalPanel,
  Spinner,
  TextArea,
  TextInput,
} from '../components/ui.js';
import { formatDate, formatDateTime } from '../lib/format.js';

export function ProcurementCard({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor } = useSession();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const procurement = workOrder.procurement;
  const [form, setForm] = useState({
    prNumber: procurement?.prNumber ?? '',
    poNumber: procurement?.poNumber ?? '',
    pcard: procurement?.pcard ?? false,
  });

  useEffect(() => {
    setForm({
      prNumber: procurement?.prNumber ?? '',
      poNumber: procurement?.poNumber ?? '',
      pcard: procurement?.pcard ?? false,
    });
  }, [procurement?.prNumber, procurement?.poNumber, procurement?.pcard]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}/procurement`, {
        method: 'PUT',
        body: {
          prNumber: form.prNumber.trim() || undefined,
          poNumber: form.poNumber.trim() || undefined,
          pcard: form.pcard,
        },
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Procurement could not be saved. Please try again.',
      );
    },
  });

  if (!isContractor || !procurement) return null;
  const relevant = ['procurement', 'repair_scheduled', 'work_in_progress', 'completed', 'closed'];
  if (!relevant.includes(workOrder.status) && workOrder.status !== 'on_hold') return null;

  const mayManage =
    can(PERMISSIONS.PROCUREMENT_MANAGE) && !['closed', 'cancelled'].includes(workOrder.status);
  const complete = procurement.completedAt !== null;

  if (editing) {
    return (
      <Card title="Procurement (PR / PO)">
        <div className="flex flex-col gap-4">
          {error && <Alert variant="error">{error}</Alert>}
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.pcard}
              onChange={(event) => setForm({ ...form, pcard: event.target.checked })}
            />
            <span>Paid by P-card — no Purchase Requisition or Purchase Order is needed.</span>
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Purchase Requisition (PR) number"
              htmlFor="prNumber"
              hint="Enter once the PR has been submitted."
            >
              <TextInput
                id="prNumber"
                value={form.prNumber}
                onChange={(event) => setForm({ ...form, prNumber: event.target.value })}
              />
            </Field>
            <Field
              label="Purchase Order (PO) number"
              htmlFor="poNumber"
              hint="Entering the PO completes procurement and emails the facility to schedule."
            >
              <TextInput
                id="poNumber"
                value={form.poNumber}
                onChange={(event) => setForm({ ...form, poNumber: event.target.value })}
              />
            </Field>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              loading={save.isPending}
            >
              Save procurement
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Procurement (PR / PO)"
      actions={
        mayManage && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {procurement.prNumber || procurement.poNumber || procurement.pcard
              ? 'Edit procurement'
              : 'Record procurement'}
          </Button>
        )
      }
    >
      {!procurement.prNumber && !procurement.poNumber && !procurement.pcard ? (
        <EmptyState
          title="Awaiting PR / PO"
          description={
            mayManage
              ? 'Record the Purchase Requisition and Purchase Order numbers, or tick Paid by P-card. The facility is emailed to schedule the repair as soon as procurement is complete.'
              : 'An administrator records the PR/PO (or P-card) here. You will be emailed to schedule the repair once that is done.'
          }
        />
      ) : (
        <DefinitionList
          items={[
            {
              label: 'Status',
              value: complete
                ? workOrder.kind === 'supplies'
                  ? 'Complete — ready to order'
                  : 'Complete — ready to schedule'
                : 'PR submitted, awaiting PO',
            },
            { label: 'Payment', value: procurement.pcard ? 'P-card (no PR/PO)' : 'PR / PO' },
            { label: 'PR number', value: procurement.prNumber },
            { label: 'PO number', value: procurement.poNumber },
            { label: 'Completed', value: formatDateTime(procurement.completedAt) },
          ]}
        />
      )}
    </Card>
  );
}

/** Supplies orders: record that the items were ordered on the chosen payment method. */
function OrderCard({ workOrder }: { workOrder: WorkOrderDetail }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [orderReference, setOrderReference] = useState(workOrder.orderReference ?? '');
  const [orderedAt, setOrderedAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const place = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}/order`, {
        method: 'PUT',
        body: {
          orderReference: orderReference.trim() || undefined,
          orderedAt: orderedAt || undefined,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The order could not be recorded. Please try again.',
      );
    },
  });

  // USPS never receives the payment method or order details; nothing to show them here.
  if (workOrder.paymentMethod === undefined) return null;

  const canOrder =
    can(PERMISSIONS.EXECUTION_MANAGE) &&
    ['approved', 'repair_scheduled'].includes(workOrder.status);
  const method = workOrder.paymentMethod
    ? SUPPLIES_PAYMENT_METHOD_LABELS[workOrder.paymentMethod]
    : null;

  return (
    <Card title="Order">
      <div className="flex flex-col gap-4">
        {error && <Alert variant="error">{error}</Alert>}
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Payment method (VGS only)
            </dt>
            <dd className="text-slate-900">{method ?? 'Not recorded'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Order placed
            </dt>
            <dd className="text-slate-900">
              {workOrder.orderedAt
                ? `${formatDate(workOrder.orderedAt)}${workOrder.orderReference ? ` — ref. ${workOrder.orderReference}` : ''}`
                : 'Not yet'}
            </dd>
          </div>
        </dl>
        {canOrder && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Order / confirmation number"
              htmlFor="orderReference"
              hint="Optional — used to match the Amazon / McMaster-Carr / P-card statement."
            >
              <TextInput
                id="orderReference"
                value={orderReference}
                onChange={(event) => setOrderReference(event.target.value)}
              />
            </Field>
            <Field label="Date ordered" htmlFor="orderedAt" hint="Leave blank for today.">
              <TextInput
                id="orderedAt"
                type="date"
                value={orderedAt}
                onChange={(event) => setOrderedAt(event.target.value)}
              />
            </Field>
            <div className="sm:col-span-2">
              <Button loading={place.isPending} onClick={() => place.mutate()}>
                {workOrder.orderedAt ? 'Update order details' : 'Order placed'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export function ScheduleSection({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor } = useSession();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mayManage =
    isContractor &&
    can(PERMISSIONS.EXECUTION_MANAGE) &&
    !['closed', 'cancelled'].includes(workOrder.status);

  const { data, isLoading } = useQuery({
    queryKey: ['schedule', workOrder.id],
    queryFn: () =>
      apiRequest<{ schedule: RepairSchedule | null }>(
        `/api/work-orders/${workOrder.id}/schedule`,
      ).then((result) => result.schedule),
  });

  const [form, setForm] = useState({
    scheduledDate: '',
    scheduledWindow: '',
    subcontractorName: '',
    notes: '',
    notesInternal: '',
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      scheduledDate: data.scheduledDate ?? '',
      scheduledWindow: data.scheduledWindow ?? '',
      subcontractorName: data.subcontractorName ?? '',
      notes: data.notes ?? '',
      notesInternal: data.notesInternal ?? '',
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}/schedule`, {
        method: 'PUT',
        body: {
          scheduledDate: form.scheduledDate,
          scheduledWindow: form.scheduledWindow.trim() || undefined,
          subcontractorName: form.subcontractorName.trim() || undefined,
          notesCustomerVisible: form.notes.trim() || undefined,
          notesInternal: form.notesInternal.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['schedule', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The schedule could not be saved. Please try again.',
      );
    },
  });

  if (isLoading) return <Spinner label="Loading schedule" />;

  // Supplies orders are ordered and received, not scheduled.
  if (workOrder.kind === 'supplies') return <OrderCard workOrder={workOrder} />;

  if (editing) {
    return (
      <Card title="Repair schedule">
        <div className="flex flex-col gap-4">
          {error && <Alert variant="error">{error}</Alert>}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Scheduled date" htmlFor="scheduledDate" required>
              <TextInput
                id="scheduledDate"
                type="date"
                required
                value={form.scheduledDate}
                onChange={(event) => setForm({ ...form, scheduledDate: event.target.value })}
              />
            </Field>
            <Field label="Time window" htmlFor="scheduledWindow" hint="Visible to USPS.">
              <TextInput
                id="scheduledWindow"
                placeholder="8:00 AM – 12:00 PM"
                value={form.scheduledWindow}
                onChange={(event) => setForm({ ...form, scheduledWindow: event.target.value })}
              />
            </Field>
          </div>

          <Field
            label="Notes for USPS"
            htmlFor="scheduleNotes"
            hint="Access needs, expected disruption, points of contact."
          >
            <TextArea
              id="scheduleNotes"
              rows={3}
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
            />
          </Field>

          <InternalPanel title="VGS internal scheduling detail">
            <div className="flex flex-col gap-3">
              <Field label="Subcontractor" htmlFor="subcontractorName">
                <TextInput
                  id="subcontractorName"
                  value={form.subcontractorName}
                  onChange={(event) => setForm({ ...form, subcontractorName: event.target.value })}
                />
              </Field>
              <Field label="Internal notes" htmlFor="scheduleInternalNotes">
                <TextArea
                  id="scheduleInternalNotes"
                  rows={3}
                  value={form.notesInternal}
                  onChange={(event) => setForm({ ...form, notesInternal: event.target.value })}
                />
              </Field>
            </div>
          </InternalPanel>

          <div className="flex gap-2">
            <Button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              loading={save.isPending}
              disabled={form.scheduledDate === ''}
            >
              Save schedule
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Repair schedule"
      actions={
        mayManage && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {data ? 'Edit schedule' : 'Schedule repair'}
          </Button>
        )
      }
    >
      {!data ? (
        <EmptyState
          title="Not scheduled yet"
          description={
            isContractor
              ? 'Schedule the repair once the proposal has been approved.'
              : 'VGS will post the scheduled date once the work is booked.'
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <DefinitionList
            items={[
              { label: 'Scheduled date', value: formatDate(data.scheduledDate) },
              { label: 'Time window', value: data.scheduledWindow },
              { label: 'Work started', value: formatDateTime(data.actualStartAt) },
              { label: 'Work finished', value: formatDateTime(data.actualCompletionAt) },
            ]}
          />

          {data.notes && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Notes
              </h3>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{data.notes}</p>
            </div>
          )}

          {data.notesInternal !== undefined && (
            <InternalPanel title="VGS internal scheduling detail">
              <p className="text-sm text-slate-800">
                Subcontractor: {data.subcontractorName ?? 'none'}
              </p>
              {data.notesInternal && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                  {data.notesInternal}
                </p>
              )}
            </InternalPanel>
          )}
        </div>
      )}
    </Card>
  );
}
